import React, {useEffect, useImperativeHandle, forwardRef, useRef} from "react";
import {useImmer} from 'use-immer';
import {useSelector} from 'react-redux';
import {cn} from '@/utils';
import useDataLayer from "@/utils/useDataLayer";

import Create from './Create';
import Upload from './Upload';
import Apply from './Apply';
import DpleCta from './DpleCta';
import {appSelector, dpleEnabledSelector} from '../../../slices/appSlice';
import {
  matrixSelector,
  pageOptionByIdSelector
} from '../../../slices/matrixSlice';

const isMobile = window.matchMedia('(max-width: 639px)').matches;

const Dpo = forwardRef(({ trackingRef }, ref) => {
  // DPLE template-led mode replaces the multi-step Create / Upload / Apply
  // modal with a single CTA that finalises the predesign via the SDK and
  // hands off to Magento's existing snapshot-based entry. The QAU branches
  // below stay reachable for every other configurator page. The render
  // branch is at the bottom so the existing hooks (useEffect /
  // useImperativeHandle / useRef) keep firing in their original order — a
  // pre-hook early-return would violate rules-of-hooks.
  const dpleEnabled = useSelector(dpleEnabledSelector);

  const {
    dpoQuote,
    baseUrl,
    code,
    baseSku,
    productId: product,
    webproductId: webproduct,
    currentOptions,
    productId,
    defaultConfig,
    customQuantities,
    optionsOverwrite,
    prices: {values: priceValues},
    productTags,
    isFetching,
    customShape: { dpoData, addToCartFormData },
    config: { is_custom_shape = false },
    AveryCustom: { client },
  } = useSelector(matrixSelector);
  const {dpoSettings} = useSelector(appSelector);
  const shapes = useSelector(pageOptionByIdSelector('shape'));
  const pages = useSelector(pageOptionByIdSelector('pages'));
  const bookBinding = useSelector(pageOptionByIdSelector('book_binding'));
  const [pushEvent] = useDataLayer();
  const urlParams = new URLSearchParams(window.location.search);

  const showCreate = !bookBinding.selection_id.includes('premium');
  const showUpload = /prt\_.*?single/.test(pages.selection_id) && /w70/gi.test(baseSku) === false;
  const showApply = urlParams.has('showApply');
  const isOnlyCreateJourney = showApply === false && showUpload === false;
  const qty = priceValues.find((p) => p.is_selected).quantity;
  const cartForm = useRef();
  const modalBody = useRef();
  const dialogRef = useRef();

  const [componentState, updateComponentState] = useImmer({
    isModalOpen: false,
    showUploadJourney: false,
    showTitleBarInUploadJourney: false,
    titleBarLabel: Drupal.t('Start designing'),
  });

  const disableButton = currentOptions.find(opt => opt.is_out_of_stock === true) !== undefined;

  const btnAttributes = {
    variant: '1',
    full: true,
    size: 'big'
  }
  if (disableButton || isFetching) {
    btnAttributes.disabled = true;
  }

  useEffect(() => {
    window.addEventListener('keyup', keyupHandler);

    return () => {
      window.removeEventListener('keyup', keyupHandler);
    };
  }, []);

  useEffect(() => {
    if (componentState.isModalOpen && dialogRef.current) {
      dialogRef.current.showModal();
    }
  }, [componentState.isModalOpen]);

  const openModal = () => {
    if (!isFetching) {
      /* Adding || client turns off the DPO journey for AveryCustom */
      if (is_custom_shape || client) {
        startUploadJourney();
      } else if(isOnlyCreateJourney) {
        startCreateJourney();
        return;
      } else {
        if (trackingRef.current) {
          trackingRef.current.trackStartDesigning();
          trackingRef.current.trackFinishConfigurator();
        }
      }
      updateComponentState((draft) => {
        draft.isModalOpen = true;
      });

      document.body.style.overflow = 'hidden';
    }
  };

  function keyupHandler(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      closeModal();
    }
  }

  const closeModal = () => {
    if (dialogRef.current) dialogRef.current.close();
    updateComponentState((draft) => {
      draft.isModalOpen = false;
      draft.showUploadJourney = false;
      draft.showTitleBarInUploadJourney = false;
      draft.titleBarLabel = Drupal.t('Start designing');
    });
    document.body.style.removeProperty('overflow');
  };

  const startCreateJourney = () => {
    const { design_with_quote_endpoints, canvas_with_quote_endpoints } = dpoSettings;
    const {
      sku,
      labelHeight: height,
      labelWidth: width,
      cornerRadius,
      labelShape: shape,
      addWhiteColor = '',
    } = dpoQuote;

    const endPoint = sku.toLowerCase().includes('custom') ? canvas_with_quote_endpoints.custom_size : design_with_quote_endpoints.standard;

    const queryParams = new URLSearchParams({
      sku,
      height,
      width,
      cornerRadius,
      shape,
      addWhiteColor: addWhiteColor === 'Y',
    });

    pushEvent({
      event: "triggerEvent",
      eventCategory: "DPO",
      eventAction: "Configurator-Create",
      eventLabel: "Create-Start"
    });

    trackingRef.current?.trackStartDesigningCta('Create Design');

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${endPoint}&${queryParams}`;
    form.enctype = 'multipart/form-data';

    const quoteField = document.createElement('textarea');
    quoteField.name = 'quote';
    quoteField.id = 'quote';
    quoteField.style.display = 'none';
    quoteField.readOnly = true;
    quoteField.value = JSON.stringify(dpoQuote);

    form.appendChild(quoteField);
    document.body.appendChild(form);
    form.submit();
  };

  const startUploadJourney = () => {
    pushEvent({
      event: "triggerEvent",
      eventCategory: "DPO",
      eventAction: "Configurator-Upload",
      eventLabel: "Upload-Start",
    });

    if (trackingRef.current) {
      trackingRef.current.trackStartDesigningCta('Upload Image');
    }

    updateComponentState((draft) => {
      draft.showUploadJourney = true;
    });
  };

  useEffect(() => {
    if (is_custom_shape && dpoData) {
      closeModal();
    }
  }, [is_custom_shape, dpoData]);

  useImperativeHandle(ref, () => ({
    startQAU() {
      openModal();
    }
  }));

  if (dpleEnabled) {
    return <DpleCta trackingRef={trackingRef} />;
  }

  return (
    <>
      <Choose>
        <When condition={dpoData}>
          <form
            ref={cartForm}
            action={urlParams.get('use_dpo_proxy') ? '/dpo_proxy.php' : baseUrl + '/dpo/product/directAddToCart'}
            method="POST"
          >
            <ft-button
              class='button-configurator-completed'
              onClick={(e) => {
                if (trackingRef.current?.trackFinishConfigurator) {
                  trackingRef.current.trackFinishConfigurator();
                }
                e.target.disabled = "true";
                cartForm.current.submit();
              }}
              {...btnAttributes}
            >
              {Drupal.t('Add to basket')}
            </ft-button>
            {
              currentOptions.map(({id, value, is_product_part}, i) => {
                let inputId = `print_options[${id}][${id}][]`;
                if (is_product_part) {
                  inputId = `print_options[PP_${code}][${id}][]`;
                }
                return <input key={id} type='hidden' name={inputId} id={inputId} value={value}/>;
              })
            }
            {
              Object.keys(addToCartFormData).map((key) => (
                <input key={key} type='hidden' name={key} id={key} value={addToCartFormData[key]}/>
              ))
            }
            <input key='qty' type='hidden' name='qty' id='qty' value={qty}/>
          </form>
        </When>
        <Otherwise>
          <ft-button
            class='button-configurator-completed'
            onClick={openModal}
            {...btnAttributes}
          >
            {is_custom_shape ? Drupal.t('Upload Artwork') : Drupal.t('Start designing')}
          </ft-button>
        </Otherwise>
      </Choose>
      <If condition={componentState.isModalOpen}>
        <dialog
          ref={dialogRef}
          id="configurator-modal-dpo"
          className="fixed inset-0 z-overlay bg-transparent p-0 w-full max-w-4xl tablet:w-11/12"
          onClick={(e) => {
            if (modalBody.current && (e.target === modalBody.current || e.target === modalBody.current.parentElement)) {
              closeModal();
            }
          }}
        >
          <div
            ref={modalBody}
            className="relative overflow-hidden shadow w-full h-screen tablet:max-h-[calc(100vh-5rem)]"
          >
            <Choose>
              <When condition={componentState.showUploadJourney}>
                <Choose>
                  <When condition={componentState.showTitleBarInUploadJourney}>
                    <div
                      className="sticky z-cover top-0 h-[55px] flex items-center justify-between bg-brand-1 pl-2xl pr-sm">
                      <div className="font-semibold text-base text-white">
                        {componentState.titleBarLabel}
                      </div>
                      <button
                        onClick={closeModal}
                        type="button"
                        className="bg-transparent border-none rounded-full h-xl w-xl"
                        aria-label={Drupal.t('Close')}
                      >
                        <ft-icon color="white" name="close" width={24} height={24}></ft-icon>
                      </button>
                    </div>
                  </When>
                  <Otherwise>
                    <div className="absolute top-0 right-0 p-3xs mr-3xs">
                      <ft-button ghost="" icon="" onClick={closeModal} type="button">
                        <ft-icon color="white" name="close" width="24"></ft-icon>
                        <span slot="a11y">{Drupal.t('Close')}</span>
                      </ft-button>
                    </div>
                  </Otherwise>
                </Choose>
                <div
                  className="bg-white w-full h-screen overflow-y-auto overflow-x-hidden max-h-[calc(100vh-3rem)] laptop:h-auto"
                >
                  <Upload
                    startUploadJourney={componentState.showUploadJourney}
                    {...{
                      dpoSettings,
                      dpoQuote,
                      shapes,
                      baseUrl,
                      code,
                      product,
                      customQuantities,
                      webproduct,
                      currentOptions,
                      isMobile,
                      qty,
                      productTags,
                      is_custom_shape,
                      productId,
                      defaultConfig,
                      optionsOverwrite,
                      closeModal,
                      showTitleBar: (show, newTitle) => updateComponentState((draft) => {
                        draft.showTitleBarInUploadJourney = show;
                        if (newTitle) {
                          draft.titleBarLabel = newTitle;
                        }
                      })
                    }}
                  />
                </div>
              </When>
              <Otherwise>
                <div
                  className="sticky z-cover top-0 h-[55px] flex items-center justify-between bg-brand-1 pl-2xl pr-sm">
                  <div className="font-semibold text-base text-white">
                    {componentState.titleBarLabel}
                  </div>
                  <button
                    onClick={closeModal}
                    type="button"
                    className="bg-transparent border-none rounded-full h-xl w-xl"
                    aria-label={Drupal.t('Close')}
                  >
                    <ft-icon color="white" name="close" width={24} height={24}></ft-icon>
                  </button>
                </div>
                <div
                  className="bg-white w-full p-lg laptop:p-xl overflow-y-auto overflow-x-hidden max-h-[calc(100vh-4rem)] tablet:max-h-[calc(100vh-9rem)]">
                  <div
                    className="grid grid-cols-1 tablet:grid-flow-col tablet:auto-cols-fr gap-xl">
                    <If condition={showCreate}>
                      <Create {...{triggerStartCreateJourney: startCreateJourney}} />
                    </If>
                    <If condition={showUpload}>
                      <Upload
                        startUploadJourney={componentState.showUploadJourney}
                        triggerStartUploadJourney={startUploadJourney}
                        {...{
                          dpoSettings, dpoQuote, showTitleBar: (show, newTitle) => updateComponentState((draft) => {
                            draft.showTitleBarInUploadJourney = show;
                            if (newTitle) {
                              draft.titleBarLabel = newTitle;
                            }
                          })
                        }}
                      />
                    </If>
                    <If condition={showApply}>
                      <Apply {...{dpoSettings, dpoQuote, trackingRef}} />
                    </If>
                  </div>
                </div>
              </Otherwise>
            </Choose>
          </div>
        </dialog>
      </If>
    </>
  );
});

export default Dpo;
